import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSION_CATALOG, type PermissionDefinition } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { authorizationApi } from '@/services/authorization.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field, Spinner } from '@/components/ui';

/** Never offered to a tenant's own role — operator-only, see
 *  `AuthorizationService`'s own exclusion of this module server-side. */
const ASSIGNABLE_PERMISSIONS = PERMISSION_CATALOG.filter((def) => def.module !== 'platform');

function groupByModule(
  defs: readonly PermissionDefinition[],
): ReadonlyMap<string, readonly PermissionDefinition[]> {
  const map = new Map<string, PermissionDefinition[]>();
  for (const def of defs) {
    const list = map.get(def.module) ?? [];
    list.push(def);
    map.set(def.module, list);
  }
  return map;
}

/** Every permission label is a translation key with no translation yet
 *  (`permissions.<module>.<resource>.<action>`, zero locales define one) —
 *  the fallback is what actually renders today, in English, until those
 *  153 keys are translated. A known, documented gap, not a hidden one. */
function permissionLabel(def: PermissionDefinition, t: ReturnType<typeof useI18n>['t']): string {
  const fallback = `${def.resource} → ${def.action}`;
  return t(def.labelKey, { fallback });
}

/**
 * Create or edit a custom role. A system role never reaches the edit form —
 * `Role.isSystem` is "cloneable but not editable in place" — so this only
 * ever composes a tenant's own role from the fixed permission catalog
 * (decision D6: tenants may not invent new permissions).
 */
export function RoleFormPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const roleQuery = useQuery({
    queryKey: ['authorization', 'roles', id],
    queryFn: () => authorizationApi.getRole(id!),
    enabled: Boolean(id),
  });

  const rolesListQuery = useQuery({
    queryKey: ['authorization', 'roles'],
    queryFn: () => authorizationApi.listRoles(),
    enabled: !id,
  });

  const [name, setName] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string> | null>(null);
  const [cloneFromId, setCloneFromId] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  const existing = roleQuery.data;
  const effectiveName = name ?? existing?.name ?? '';
  const effectiveDescription = description ?? existing?.description ?? '';
  const effectiveSelected = selected ?? new Set(existing?.permissions ?? []);

  const grouped = useMemo(() => groupByModule(ASSIGNABLE_PERMISSIONS), []);

  const cloneFrom = useMutation({
    mutationFn: (roleId: string) => authorizationApi.getRole(roleId),
    onSuccess: (role) => setSelected(new Set(role.permissions)),
  });

  const save = useMutation({
    mutationFn: () => {
      const permissionKeys = [...effectiveSelected];
      const payload = {
        name: effectiveName.trim(),
        description: effectiveDescription.trim() || null,
        permissionKeys,
      };
      return id ? authorizationApi.updateRole(id, payload) : authorizationApi.createRole(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['authorization', 'roles'] });
      void navigate('/administration/roles');
    },
    onError: (error: unknown) => {
      setSaveError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  function toggle(key: string): void {
    const next = new Set(effectiveSelected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    if (!effectiveName.trim() || effectiveSelected.size === 0) return;
    save.mutate();
  }

  if (id && roleQuery.isPending) {
    return <Spinner label={t('common.states.loading')} />;
  }
  if (id && existing?.isSystem) {
    return (
      <div className="page">
        <Alert tone="warning">{t('administration.roles.systemNotEditable')}</Alert>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">
          {id ? t('administration.roles.editTitle') : t('administration.roles.newTitle')}
        </h1>
      </header>

      <form className="settings-card" onSubmit={handleSubmit}>
        {saveError && <Alert tone="danger">{saveError}</Alert>}

        <div className="form-grid">
          <Field
            label={t('administration.roles.name')}
            name="roleName"
            value={effectiveName}
            onChange={(event) => setName(event.target.value)}
            required
          />
          <Field
            label={t('administration.roles.description')}
            name="roleDescription"
            value={effectiveDescription}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>

        {!id && (rolesListQuery.data?.length ?? 0) > 0 && (
          <div className="field">
            <label className="field-label" htmlFor="cloneFrom">
              {t('administration.roles.cloneFrom')}
            </label>
            <select
              id="cloneFrom"
              className="field-input"
              value={cloneFromId}
              onChange={(event) => {
                setCloneFromId(event.target.value);
                if (event.target.value) cloneFrom.mutate(event.target.value);
              }}
            >
              <option value="">{t('administration.roles.cloneFromNone')}</option>
              {rolesListQuery.data?.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name ?? t(role.nameKey, { fallback: role.key })}
                </option>
              ))}
            </select>
          </div>
        )}

        <p className="page-count">
          {t('administration.roles.selectedCount', {
            values: { count: effectiveSelected.size },
            fallback: `${effectiveSelected.size} permissions selected`,
          })}
        </p>

        {[...grouped.entries()].map(([module, defs]) => {
          const checkedCount = defs.filter((def) => effectiveSelected.has(def.key)).length;
          return (
            <details key={module} className="people-drawer-section">
              <summary>
                {module} ({checkedCount}/{defs.length})
              </summary>
              <div className="people-drawer-section-body">
                {defs.map((def) => (
                  <label key={def.key} className="checkbox">
                    <input
                      type="checkbox"
                      checked={effectiveSelected.has(def.key)}
                      onChange={() => toggle(def.key)}
                    />
                    {permissionLabel(def, t)}
                  </label>
                ))}
              </div>
            </details>
          );
        })}

        <div className="card-fields-row">
          <Button
            type="button"
            variant="ghost"
            onClick={() => void navigate('/administration/roles')}
          >
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {t('common.actions.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
